import { useState, useEffect, useCallback, useMemo } from 'react';
import API from '../api/axios';
import TaskItem from './TaskItem';
import NoteItem from './NoteItem';
import TaskModal from './TaskModal';
import useSortPreference from '../hooks/useSortPreference';
import useHideCompletedPreference from '../hooks/useHideCompletedPreference';

const priorityOrder = { Low: 0, Medium: 1, High: 2 };
const statusOrder = { 'Not Started': 0, Preparing: 1, 'In Progress': 2, 'On Hold': 3, Completed: 4 };

function compareSortValues(a, b, sortCol, sortDir) {
  let valA, valB;
  switch (sortCol) {
    case '_type':
      valA = a._type; valB = b._type;
      break;
    case 'task_number':
      valA = a.task_number ?? null; valB = b.task_number ?? null;
      break;
    case 'name':
      valA = (a.name || '').toLowerCase(); valB = (b.name || '').toLowerCase();
      break;
    case 'priority':
      valA = a.priority != null ? priorityOrder[a.priority] : null;
      valB = b.priority != null ? priorityOrder[b.priority] : null;
      break;
    case 'status':
      valA = a.status != null ? statusOrder[a.status] : null;
      valB = b.status != null ? statusOrder[b.status] : null;
      break;
    case 'start_date':
      valA = a.start_date ? new Date(a.start_date) : null;
      valB = b.start_date ? new Date(b.start_date) : null;
      break;
    case 'end_date':
      valA = a.end_date ? new Date(a.end_date) : null;
      valB = b.end_date ? new Date(b.end_date) : null;
      break;
    default:
      return 0;
  }
  if (valA == null && valB == null) return 0;
  if (valA == null) return 1;
  if (valB == null) return -1;
  if (valA < valB) return sortDir === 'asc' ? -1 : 1;
  if (valA > valB) return sortDir === 'asc' ? 1 : -1;
  return 0;
}

export default function TaskList({ folderId, folderName, folderRefreshKey, onEditNote }) {
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [hideCompleted, setHideCompleted] = useHideCompletedPreference(
    folderId ? `focusly-folder-hide-completed-${folderId}` : null
  );
  const { sortCol, sortDir, handleSort } = useSortPreference(folderId ? `focusly-folder-sort-${folderId}` : null);
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchTasks = useCallback(async () => {
    if (!folderId) { setTasks([]); return; }
    const { data } = await API.get('/tasks', { params: { folder_id: folderId } });
    setTasks(data);
  }, [folderId]);

  const fetchNotes = useCallback(async () => {
    if (!folderId) { setNotes([]); return; }
    const { data } = await API.get('/notes', { params: { folder_id: folderId } });
    setNotes(data);
  }, [folderId]);

  useEffect(() => { fetchTasks(); fetchNotes(); }, [fetchTasks, fetchNotes]);

  // Refetch when a task is moved into/out of this folder from elsewhere (e.g.
  // dragged onto a folder in the sidebar tree).
  useEffect(() => {
    const onChanged = () => fetchTasks();
    window.addEventListener('focusly:tasks-changed', onChanged);
    return () => window.removeEventListener('focusly:tasks-changed', onChanged);
  }, [fetchTasks]);

  useEffect(() => { setSelectedIds([]); }, [folderId]);

  useEffect(() => {
    const fetchFolders = async () => {
      const { data } = await API.get('/folders');
      setFolders(data);
    };
    fetchFolders();
  }, [folderRefreshKey]);

  useEffect(() => {
    const fetchTags = async () => {
      const { data } = await API.get('/tags');
      setTags(data);
    };
    fetchTags();
  }, []);

  const handleCreate = () => {
    setEditTask(null);
    setShowModal(true);
  };

  const handleEdit = (task) => {
    setEditTask(task);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const isBulk = selectedIds.length > 1 && selectedIds.includes(id);
    if (isBulk) {
      if (!window.confirm(`Delete ${selectedIds.length} tasks?`)) return;
      await Promise.all(selectedIds.map((sid) => API.delete(`/tasks/${sid}`)));
      setShowModal(false);
      setSelectedIds([]);
    } else {
      if (!window.confirm('Delete this task?')) return;
      await API.delete(`/tasks/${id}`);
      setShowModal(false);
      setSelectedIds((prev) => prev.filter((sid) => sid !== id));
    }
    fetchTasks();
  };

  const handleDeleteNote = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    await API.delete(`/notes/${id}`);
    fetchNotes();
  };

  const handleSave = async (taskData) => {
    if (editTask) {
      if (selectedIds.length > 1 && selectedIds.includes(editTask.id)) {
        const { name, description, ...bulkUpdates } = taskData;
        await API.patch('/tasks/bulk', { ids: selectedIds, updates: bulkUpdates });
      } else {
        await API.put(`/tasks/${editTask.id}`, taskData);
      }
    } else {
      await API.post('/tasks', taskData);
    }
    setShowModal(false);
    fetchTasks();
  };

  const handleSaveAndAddAnother = async (taskData) => {
    await API.post('/tasks', taskData);
    fetchTasks();
  };

  const filteredTasks = hideCompleted ? tasks.filter((t) => t.status !== 'Completed') : tasks;
  const mergedItems = useMemo(() => {
    const items = [
      ...filteredTasks.map((t) => ({ ...t, _type: 'task' })),
      ...notes.map((n) => ({ ...n, _type: 'note' })),
    ];
    if (sortCol) {
      items.sort((a, b) => compareSortValues(a, b, sortCol, sortDir));
    } else {
      items.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }
    return items;
  }, [filteredTasks, notes, sortCol, sortDir]);

  const visibleTaskIds = useMemo(
    () => mergedItems.filter((i) => i._type === 'task').map((t) => t.id),
    [mergedItems]
  );
  const allSelected = visibleTaskIds.length > 0 && selectedIds.length === visibleTaskIds.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : visibleTaskIds);
  };

  const sortIndicator = (col) => {
    if (sortCol !== col) return ' \u2195';
    return sortDir === 'asc' ? ' \u2191' : ' \u2193';
  };

  if (!folderId) {
    return (
      <div className="d-flex align-items-center justify-content-center h-100 text-muted">
        Select a folder to view items
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">{folderName}</h5>
        <div>
          {selectedIds.length > 0 && (
            <span className="text-muted me-2 small">{selectedIds.length} selected</span>
          )}
          <button
            className={`btn btn-sm me-2 ${hideCompleted ? 'btn-outline-secondary' : 'btn-secondary'}`}
            onClick={() => setHideCompleted(!hideCompleted)}
          >
            {hideCompleted ? 'Show Completed' : 'Hide Completed'}
          </button>
          <button className="btn btn-primary btn-sm me-2" onClick={handleCreate}>+ Add Task</button>
          <button className="btn btn-outline-primary btn-sm" onClick={() => onEditNote(null)}>+ Add Note</button>
        </div>
      </div>

      {mergedItems.length === 0 ? (
        <p className="text-muted">{hideCompleted ? 'No incomplete items in this folder' : 'No items in this folder'}</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ whiteSpace: 'nowrap' }}>
            <thead>
              <tr>
                <th className="ps-0" style={{ width: 24 }}></th>
                <th style={{ width: 60, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('_type')}>Type{sortIndicator('_type')}</th>
                <th style={{ width: 60, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('task_number')}>ID{sortIndicator('task_number')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('name')}>Name{sortIndicator('name')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('priority')}>Priority{sortIndicator('priority')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>Status{sortIndicator('status')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('start_date')}>Start{sortIndicator('start_date')}</th>
                <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('end_date')}>End{sortIndicator('end_date')}</th>
                <th style={{ width: 100 }}></th>
                <th style={{ width: 32 }} className="text-center">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleSelectAll}
                    disabled={visibleTaskIds.length === 0}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {mergedItems.map((item) =>
                item._type === 'task' ? (
                  <TaskItem
                    key={item.id}
                    task={item}
                    onEdit={handleEdit}
                    selectable
                    selected={selectedIds.includes(item.id)}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleSelect}
                  />
                ) : (
                  <NoteItem key={item.id} note={item} onEdit={() => onEditNote(item)} onDelete={handleDeleteNote} selectable />
                )
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <TaskModal
          task={editTask}
          onSave={handleSave}
          onSaveAndAddAnother={handleSaveAndAddAnother}
          onDelete={handleDelete}
          onClose={() => setShowModal(false)}
          folders={folders}
          currentFolderId={folderId}
          tags={tags}
          bulkCount={editTask && selectedIds.length > 1 && selectedIds.includes(editTask.id) ? selectedIds.length : 1}
        />
      )}
    </div>
  );
}
